import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import { apiRequest } from '../../../api/client';
import {
  FIELD_TYPE_LABELS,
  WORK_ORDER_FIELD_TYPES,
  defaultTemplate,
  newFieldId,
  newSectionKey,
  type WorkOrderFormTemplate
} from '../../../lib/workOrderForm';
import '../quotes/quote-generator.css';
import './work-order-forms.css';

export default function WorkOrderFormBuilder(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<WorkOrderFormTemplate>(defaultTemplate());
  const [busy, setBusy] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    apiRequest<{ success: boolean; data: WorkOrderFormTemplate }>(`/api/work-order-form-templates/${id}`)
      .then((res) => setTemplate(res.data))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load template'))
      .finally(() => setBusy(false));
  }, [id]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      if (id) {
        await apiRequest(`/api/work-order-form-templates/${id}`, { method: 'PATCH', json: template });
      } else {
        const created = await apiRequest<{ success: boolean; data: WorkOrderFormTemplate }>(
          '/api/work-order-form-templates',
          { method: 'POST', json: template }
        );
        navigate(`/admin/work-order-forms/${created.data._id}`, { replace: true });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (busy) {
    return (
      <AppShell>
        <div className="wo-form-page"><p>Loading…</p></div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="wo-form-page">
        <header className="tech-hero">
          <div>
            <Link to="/admin/work-order-forms" className="dash-btn dash-btn-ghost" style={{ marginBottom: 8, display: 'inline-block', color: 'var(--dash-muted)' }}>
              ← Back
            </Link>
            <h1 className="tech-title">{id ? 'Edit form template' : 'New form template'}</h1>
          </div>
          <div className="tech-actions">
            <button type="button" className="btn-primary" disabled={saving} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save template'}
            </button>
          </div>
        </header>

        {error ? <div className="banner banner-error">{error}</div> : null}

        <div className="wo-form-grid-admin">
          <div className="wo-form-section-card">
            <div className="wo-form-row">
              <label>
                <span className="wo-dynamic-label">Template name</span>
                <input
                  className="wo-dynamic-input"
                  value={template.name}
                  onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                />
              </label>
              <label>
                <span className="wo-dynamic-label">Status</span>
                <select
                  className="wo-dynamic-select"
                  value={template.status || 'active'}
                  onChange={(e) => setTemplate({ ...template, status: e.target.value as 'active' | 'archived' })}
                >
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
              </label>
            </div>
            <label>
              <span className="wo-dynamic-label">Description</span>
              <textarea
                className="wo-dynamic-textarea"
                value={template.description || ''}
                onChange={(e) => setTemplate({ ...template, description: e.target.value })}
              />
            </label>
          </div>

          {template.sections.map((section, sIdx) => (
            <div key={section.key} className="wo-form-section-card">
              <div className="wo-form-section-head">
                <h3 style={{ margin: 0 }}>Section {sIdx + 1}</h3>
                <button
                  type="button"
                  className="wof-remove-btn"
                  onClick={() =>
                    setTemplate({
                      ...template,
                      sections: template.sections.filter((s) => s.key !== section.key)
                    })
                  }
                >
                  Remove section
                </button>
              </div>
              <div className="wo-form-row">
                <input
                  className="wo-dynamic-input"
                  value={section.title}
                  onChange={(e) => {
                    const sections = [...template.sections];
                    sections[sIdx] = { ...section, title: e.target.value };
                    setTemplate({ ...template, sections });
                  }}
                />
              </div>
              {section.fields.map((field, fIdx) => (
                <div key={field.fieldId} className="wo-form-field-card">
                  <div className="wo-form-field-head">
                    <span className="wo-form-field-title">Question {fIdx + 1}</span>
                    <button
                      type="button"
                      className="wof-remove-btn"
                      aria-label={`Remove question ${fIdx + 1}`}
                      onClick={() => {
                        const sections = [...template.sections];
                        sections[sIdx] = {
                          ...section,
                          fields: section.fields.filter((f) => f.fieldId !== field.fieldId)
                        };
                        setTemplate({ ...template, sections });
                      }}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="wo-form-row">
                    <input
                      className="wo-dynamic-input"
                      placeholder="Question label"
                      value={field.label}
                      onChange={(e) => {
                        const sections = [...template.sections];
                        const fields = [...section.fields];
                        fields[fIdx] = { ...field, label: e.target.value };
                        sections[sIdx] = { ...section, fields };
                        setTemplate({ ...template, sections });
                      }}
                    />
                    <select
                      className="wo-dynamic-select"
                      value={field.type}
                      onChange={(e) => {
                        const sections = [...template.sections];
                        const fields = [...section.fields];
                        fields[fIdx] = { ...field, type: e.target.value as typeof field.type };
                        sections[sIdx] = { ...section, fields };
                        setTemplate({ ...template, sections });
                      }}
                    >
                      {WORK_ORDER_FIELD_TYPES.map((t) => (
                        <option key={t} value={t}>{FIELD_TYPE_LABELS[t]}</option>
                      ))}
                    </select>
                  </div>
                  <label className="wo-check-row">
                    <input
                      type="checkbox"
                      checked={Boolean(field.required)}
                      onChange={(e) => {
                        const sections = [...template.sections];
                        const fields = [...section.fields];
                        fields[fIdx] = { ...field, required: e.target.checked };
                        sections[sIdx] = { ...section, fields };
                        setTemplate({ ...template, sections });
                      }}
                    />
                    Required
                  </label>
                  {(field.type === 'select' || field.type === 'multi_select') ? (
                    <textarea
                      className="wo-dynamic-textarea"
                      placeholder="One option per line"
                      value={(field.options || []).join('\n')}
                      onChange={(e) => {
                        const sections = [...template.sections];
                        const fields = [...section.fields];
                        fields[fIdx] = {
                          ...field,
                          options: e.target.value.split('\n').map((v) => v.trim()).filter(Boolean)
                        };
                        sections[sIdx] = { ...section, fields };
                        setTemplate({ ...template, sections });
                      }}
                    />
                  ) : null}
                </div>
              ))}
              <button
                type="button"
                className="wo-add-question-btn"
                onClick={() => {
                  const sections = [...template.sections];
                  sections[sIdx] = {
                    ...section,
                    fields: [...section.fields, { fieldId: newFieldId(), label: 'New question', type: 'short_text' }]
                  };
                  setTemplate({ ...template, sections });
                }}
              >
                + Add question
              </button>
            </div>
          ))}

          <button
            type="button"
            className="btn-primary"
            onClick={() =>
              setTemplate({
                ...template,
                sections: [
                  ...template.sections,
                  { key: newSectionKey(), title: 'New section', fields: [] }
                ]
              })
            }
          >
            + Add section
          </button>
        </div>
      </div>
    </AppShell>
  );
}
